import { LitElement, html, css } from 'lit';
import { getConfig } from '../config.js';

/**
 * Normalise l'heure au format HH:MM
 */
function normalizeTime(str) {
  if (!str) return '';
  // Remplace les séparateurs courants comme 'h', 'H', '.', ',' par ':'
  let cleaned = str.replace(/[hH\.,]/g, ':').trim();
  
  // Si c'est juste un nombre à 3 ou 4 chiffres (ex: 1430 ou 930)
  if (!cleaned.includes(':') && /^\d{3,4}$/.test(cleaned)) {
    if (cleaned.length === 3) {
      cleaned = '0' + cleaned;
    }
    cleaned = cleaned.substring(0, 2) + ':' + cleaned.substring(2, 4);
  }

  const parts = cleaned.split(':');
  if (parts.length >= 2) {
    let h = parseInt(parts[0], 10);
    let m = parseInt(parts[1], 10);
    
    if (isNaN(h) || h < 0 || h > 23) h = 0;
    if (isNaN(m) || m < 0 || m > 59) m = 0;
    
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  return '';
}

/**
 * Composant <ui-timepicker>
 * Composant de sélection d'heure moderne avec colonnes défilantes
 */
export class UITimePicker extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Format HH:MM
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    step: { type: Number }, // Intervalle pour les minutes (ex: 5, 15, 30)
    label: { type: String, reflect: true },
    
    // États internes
    isOpen: { type: Boolean, state: true },
    selectedHour: { type: String, state: true },
    selectedMinute: { type: String, state: true },
    inputValue: { type: String, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .timepicker-container {
      position: relative;
      width: 100%;
    }

    /* Le Header (Input principal) */
    .timepicker-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .timepicker-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 40px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .timepicker-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .timepicker-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .timepicker-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .timepicker-icon {
      position: absolute;
      right: var(--ui-padding-x);
      top: 50%;
      transform: translateY(-50%);
      color: var(--ui-text-muted);
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .timepicker-container.open .timepicker-icon {
      color: var(--ui-primary);
    }

    /* Le Panel Popup */
    .timepicker-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 200px;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      overflow: hidden;
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .timepicker-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* Grille et Colonnes Défilantes */
    .timepicker-columns {
      display: grid;
      grid-template-columns: 1fr 1fr;
      height: 200px;
    }

    .timepicker-column {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      scroll-behavior: smooth;
      padding: 6px 0;
    }

    .timepicker-column::-webkit-scrollbar {
      width: 4px;
    }

    .timepicker-column::-webkit-scrollbar-thumb {
      background-color: var(--ui-border);
      border-radius: var(--ui-radius-full);
    }

    .timepicker-column:first-child {
      border-right: 1px solid var(--ui-border);
    }

    /* En-tête des colonnes */
    .column-header {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--ui-text-muted);
      text-align: center;
      padding: 6px 0;
      background-color: var(--ui-surface-hover);
      border-bottom: 1px solid var(--ui-border);
    }

    /* Items d'heures / minutes */
    .time-item {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 36px;
      min-height: 36px;
      font-size: var(--ui-font-size-base);
      cursor: pointer;
      transition: var(--ui-transition-fast);
      user-select: none;
    }

    .time-item:hover:not(.selected) {
      background-color: var(--ui-surface-hover);
      color: var(--ui-text);
    }

    .time-item.selected {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-weight: 600;
    }

    /* Backdrop pour version mobile */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    /* Style Responsive Mobile - Plein Écran / Bottom Sheet */
    @media (max-width: 576px) {
      .timepicker-container.open .backdrop {
        display: block;
      }

      .timepicker-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        transform: translateY(100%) scale(1);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
      }

      .timepicker-container.open .timepicker-panel {
        transform: translateY(0) scale(1);
        opacity: 1;
      }

      .timepicker-panel::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: 8px auto 8px auto;
      }
    }

    .timepicker-footer {
      padding: 8px 12px;
      border-top: 1px solid var(--ui-border);
      display: flex;
      justify-content: center;
      background-color: var(--ui-surface);
    }

    .btn-now {
      width: 100%;
      height: 32px;
      border: 1px solid var(--ui-primary);
      border-radius: var(--ui-radius-sm);
      background-color: transparent;
      color: var(--ui-primary);
      font-size: var(--ui-font-size-sm);
      font-weight: 600;
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .btn-now:hover {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = '';
    this.disabled = false;
    this.required = false;
    this.step = 0; // Sera surchargé par le config s'il vaut 0
    this.label = '';

    // États initiaux
    this.isOpen = false;
    this.selectedHour = '12';
    this.selectedMinute = '00';
    this.inputValue = '';

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez sélectionner une heure.', this.shadowRoot.querySelector('.timepicker-input'));
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('value')) {
      if (this.value && this.value.includes(':')) {
        const parts = this.value.split(':');
        this.selectedHour = parts[0];
        this.selectedMinute = parts[1];
        this.inputValue = this.value;
      } else {
        this.inputValue = '';
      }
    }
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
    }
  }

  _togglePanel() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closePanel();
    } else {
      this._openPanel();
    }
  }

  _openPanel() {
    this.isOpen = true;
    
    // Auto-scroll les éléments sélectionnés
    setTimeout(() => {
      this._scrollToSelected('hour');
      this._scrollToSelected('minute');
    }, 50);
  }

  _closePanel() {
    this.isOpen = false;
  }

  /**
   * Fait défiler la colonne pour centrer l'item sélectionné
   */
  _scrollToSelected(type) {
    const list = this.shadowRoot.querySelector(
      `.timepicker-column[data-type="${type}"]`
    );
    const selectedItem = list?.querySelector('.time-item.selected');
    if (list && selectedItem) {
      list.scrollTop = selectedItem.offsetTop - (list.clientHeight / 2) + (selectedItem.clientHeight / 2);
    }
  }

  _selectHour(h) {
    this.selectedHour = h;
    this._updateValue();
    setTimeout(() => this._scrollToSelected('hour'), 10);
  }

  _selectMinute(m) {
    this.selectedMinute = m;
    this._updateValue();
    setTimeout(() => this._scrollToSelected('minute'), 10);
  }

  _updateValue() {
    const newValue = `${this.selectedHour}:${this.selectedMinute}`;
    const oldValue = this.value;
    this.value = newValue;
    this.inputValue = newValue;

    if (oldValue !== newValue) {
      // Déclenche le change natif
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      // Déclenche l'événement personnalisé
      this.dispatchEvent(new CustomEvent('ui-change', {
        detail: { value: newValue },
        bubbles: true,
        composed: true
      }));
    }
  }

  _handleInput(e) {
    this.inputValue = e.target.value;
    const normalized = normalizeTime(this.inputValue);
    
    if (normalized) {
      const parts = normalized.split(':');
      this.selectedHour = parts[0];
      this.selectedMinute = parts[1];
      
      const oldValue = this.value;
      this.value = normalized;
      
      if (oldValue !== normalized) {
        this._dispatchChangeEvent(normalized);
      }
    }
  }

  _handleBlur() {
    // Si l'utilisateur quitte avec une valeur invalide, on remet la valeur précédente
    const normalized = normalizeTime(this.inputValue);
    if (normalized && normalized === this.value) {
      this.inputValue = this.value;
    } else if (this.value) {
      this.inputValue = this.value;
    } else {
      this.inputValue = '';
    }
  }

  _dispatchChangeEvent(val) {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('ui-change', {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }

  _handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this._closePanel();
    }
  }

  _selectNow() {
    const now = new Date();
    const stepSetting = this.step || getConfig('timepicker', 'step') || 15;
    let minVal = now.getMinutes();
    const roundedMin = Math.round(minVal / stepSetting) * stepSetting;
    const finalMin = roundedMin >= 60 ? 0 : roundedMin;
    const finalHour = roundedMin >= 60 ? (now.getHours() + 1) % 24 : now.getHours();

    const formattedHour = String(finalHour).padStart(2, '0');
    const formattedMinute = String(finalMin).padStart(2, '0');

    this.selectedHour = formattedHour;
    this.selectedMinute = formattedMinute;
    this._closePanel();
    this._updateValue();
  }

  render() {
    const displayPlaceholder = this.placeholder || getConfig('timepicker', 'placeholder');
    const stepSetting = this.step || getConfig('timepicker', 'step') || 15;

    // Génère les listes d'heures (00-23) et de minutes en fonction du step
    const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
    const minutes = [];
    for (let i = 0; i < 60; i += stepSetting) {
      minutes.push(String(i).padStart(2, '0'));
    }

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="timepicker-container ui-component ${this.isOpen ? 'open' : ''}">
        <!-- Soumission automatique gérée via ElementInternals -->

        <!-- Backdrop mobile -->
        <div class="backdrop" @click="${this._closePanel}"></div>

        <!-- En-tête avec input -->
        <div class="timepicker-header">
          <input 
            type="text" 
            inputmode="none"
            class="timepicker-input" part="input" 
            placeholder="${displayPlaceholder}"
            .value="${this.inputValue}"
            ?disabled="${this.disabled}"
            @focus="${this._openPanel}"
            @input="${this._handleInput}"
            @blur="${this._handleBlur}"
            @keydown="${this._handleKeyDown}"
          >
          <span class="timepicker-icon" @click="${this._togglePanel}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </span>
        </div>

        <!-- Panel de sélection -->
        <div class="timepicker-panel ${this.isOpen ? 'open' : ''}">
          <div style="display: grid; grid-template-columns: 1fr 1fr;">
            <div class="column-header">Heures</div>
            <div class="column-header">Minutes</div>
          </div>
          
          <div class="timepicker-columns">
            <!-- Colonne Heures -->
            <div class="timepicker-column" data-type="hour">
              ${hours.map(h => {
                const isSelected = h === this.selectedHour;
                return html`
                  <div 
                    class="time-item ${isSelected ? 'selected' : ''}"
                    @click="${() => this._selectHour(h)}"
                  >
                    ${h}
                  </div>
                `;
              })}
            </div>

            <!-- Colonne Minutes -->
            <div class="timepicker-column" data-type="minute">
              ${minutes.map(m => {
                const isSelected = m === this.selectedMinute;
                return html`
                  <div 
                    class="time-item ${isSelected ? 'selected' : ''}"
                    @click="${() => this._selectMinute(m)}"
                  >
                    ${m}
                  </div>
                `;
              })}
            </div>
          </div>
          
          <div class="timepicker-footer">
            <button type="button" class="btn-now" @click="${this._selectNow}">Maintenant</button>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-timepicker')) {
  customElements.define('ui-timepicker', UITimePicker);
}
